'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { StoreFooter } from '../../../components/StoreChrome';
import SiteHeader from '../../../components/SiteHeader';
import Logo from '../../../components/Logo';
import { IconCoracao, IconComentario, IconCompartilhar, IconSalvar, IconOlho } from '../../../components/SocialIcons';
import { Artigo, listarArtigos, dataArtigoBr } from '../../../lib/blog-api';

type Layout = 'post' | 'grid3' | 'lista';
const LAYOUT_KEY = 'artigos-layout';

function formatarVisitas(n: number): string {
  return n.toLocaleString('pt-BR');
}

/** Contagem de visitas com icone, reutilizada nos três layouts de card. */
function VisitasBadge({ visitas }: { visitas?: number }) {
  if (!visitas) return null;
  return (
    <span className="inline-flex items-center gap-1 text-gray-400" style={{ fontSize: '12px' }}>
      <IconOlho />
      {formatarVisitas(visitas)}
    </span>
  );
}

/** Card estilo post de feed (Instagram): header de "perfil", capa, ações e legenda. */
function ArtigoCardPost({ artigo }: { artigo: Artigo }) {
  const t = useTranslations('paginaArtigos');
  const [curtido, setCurtido] = useState(false);
  const [salvo, setSalvo] = useState(false);

  return (
    <article
      className="bg-white"
      style={{ border: '1px solid #dbdbdb', borderRadius: '8px', overflow: 'hidden' }}
    >
      <div className="flex items-center gap-3" style={{ padding: '11px 14px' }}>
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '999px',
            background: '#eeeeee',
            padding: '5px',
          }}
        >
          <Logo style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">enq.digital</p>
          {artigo.publicadoEm && (
            <p className="text-gray-500 leading-tight" style={{ fontSize: '12px' }}>
              {dataArtigoBr(artigo.publicadoEm)}
            </p>
          )}
        </div>
        <VisitasBadge visitas={artigo.visitas} />
      </div>

      <a
        href={`/artigos/${artigo.slug}`}
        className="relative block bg-gray-100"
        style={{ aspectRatio: '1/1' }}
      >
        {artigo.capa ? (
          <img src={artigo.capa} alt={artigo.titulo} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            {t('semImagem')}
          </div>
        )}
      </a>

      <div style={{ padding: '10px 14px 4px' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              aria-label={curtido ? t('descurtir') : t('curtir')}
              onClick={() => setCurtido((v) => !v)}
              style={{ color: curtido ? '#ed4956' : '#000' }}
            >
              <IconCoracao />
            </button>
            <a
              href={`/artigos/${artigo.slug}`}
              aria-label={t('verArtigo')}
              style={{ color: '#000' }}
            >
              <IconComentario />
            </a>
            <button aria-label={t('compartilhar')} style={{ color: '#000' }}>
              <IconCompartilhar />
            </button>
          </div>
          <button
            aria-label={salvo ? t('removerDosSalvos') : t('salvar')}
            onClick={() => setSalvo((v) => !v)}
            style={{ color: '#000' }}
          >
            <IconSalvar filled={salvo} />
          </button>
        </div>
      </div>

      <div style={{ padding: '6px 14px 14px' }}>
        <p className="text-sm">
          <span className="font-semibold">enq.digital</span>{' '}
          <span className="font-semibold">{artigo.titulo}.</span>{' '}
          {artigo.resumo && <span className="text-gray-700">{artigo.resumo}</span>}
        </p>
        <a
          href={`/artigos/${artigo.slug}`}
          className="inline-block mt-3 text-sm font-semibold"
          style={{ color: '#1a9fff' }}
        >
          {t('lerMateriaCompleta')}
        </a>
      </div>
    </article>
  );
}

function ArtigoCard({ artigo }: { artigo: Artigo }) {
  const t = useTranslations('paginaArtigos');
  return (
    <a href={`/artigos/${artigo.slug}`} className="flex flex-col group">
      <div
        className="relative overflow-hidden bg-gray-100"
        style={{ aspectRatio: '16/9', borderRadius: '4px' }}
      >
        {artigo.capa ? (
          <img
            src={artigo.capa}
            alt={artigo.titulo}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            {t('semImagem')}
          </div>
        )}
      </div>
      <div className="pt-4 flex-1 flex flex-col">
        {artigo.publicadoEm && (
          <p
            className="text-gray-500 mb-1"
            style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            {dataArtigoBr(artigo.publicadoEm)}
          </p>
        )}
        <h2 className="font-semibold mb-2" style={{ fontSize: '17px', lineHeight: 1.35 }}>
          {artigo.titulo}
        </h2>
        {artigo.resumo && (
          <p className="text-gray-600" style={{ fontSize: '14px', lineHeight: 1.6 }}>
            {artigo.resumo}
          </p>
        )}
        <div className="flex items-center gap-3 mt-3">
          {artigo.autor && (
            <p className="text-gray-400" style={{ fontSize: '12px' }}>
              {artigo.autor}
            </p>
          )}
          <VisitasBadge visitas={artigo.visitas} />
        </div>
      </div>
    </a>
  );
}

/** Linha horizontal: melhor para escanear muitos títulos rapidamente. */
function ArtigoLinha({ artigo }: { artigo: Artigo }) {
  const t = useTranslations('paginaArtigos');
  return (
    <a
      href={`/artigos/${artigo.slug}`}
      className="flex items-center gap-4 py-4"
      style={{ borderBottom: '1px solid #f0f0f0' }}
    >
      <div
        className="relative flex-shrink-0 overflow-hidden bg-gray-100"
        style={{ width: '120px', height: '80px', borderRadius: '4px' }}
      >
        {artigo.capa ? (
          <img src={artigo.capa} alt={artigo.titulo} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            {t('semImagem')}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        {artigo.publicadoEm && (
          <p
            className="text-gray-500"
            style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            {dataArtigoBr(artigo.publicadoEm)}
          </p>
        )}
        <p className="font-semibold" style={{ fontSize: '15px' }}>
          {artigo.titulo}
        </p>
        {artigo.resumo && (
          <p className="text-gray-500 text-sm truncate" style={{ maxWidth: '640px' }}>
            {artigo.resumo}
          </p>
        )}
        <div className="flex items-center gap-3">
          {artigo.autor && (
            <p className="text-gray-400" style={{ fontSize: '12px' }}>
              {artigo.autor}
            </p>
          )}
          <VisitasBadge visitas={artigo.visitas} />
        </div>
      </div>
    </a>
  );
}

function LayoutToggle({ layout, onChange }: { layout: Layout; onChange: (l: Layout) => void }) {
  const t = useTranslations('paginaArtigos');
  const options: { value: Layout; label: string }[] = [
    { value: 'post', label: t('layoutPost') },
    { value: 'grid3', label: t('layoutGrid') },
    { value: 'lista', label: t('layoutLista') },
  ];

  return (
    <div className="flex gap-1 flex-shrink-0" role="group" aria-label={t('layoutAria')}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className="text-xs font-medium transition-colors duration-150"
          style={{
            border: '1px solid #000',
            padding: '7px 12px',
            background: layout === o.value ? '#000' : 'transparent',
            color: layout === o.value ? '#fff' : '#000',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function ArtigosPage() {
  const t = useTranslations('paginaArtigos');
  const locale = useLocale();
  const [artigos, setArtigos] = useState<Artigo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [layout, setLayout] = useState<Layout>('post');

  useEffect(() => {
    const saved = window.localStorage.getItem(LAYOUT_KEY);
    if (saved === 'post' || saved === 'grid3' || saved === 'lista') {
      setLayout(saved);
    }
  }, []);

  function handleLayoutChange(l: Layout) {
    setLayout(l);
    window.localStorage.setItem(LAYOUT_KEY, l);
  }

  useEffect(() => {
    listarArtigos({ limite: 24, locale })
      .then(({ artigos }) => setArtigos(artigos))
      .catch(() => setErro(t('erro')))
      .finally(() => setLoading(false));
  }, [t, locale]);

  return (
    <>
      <SiteHeader />
      <main className="bg-white" style={{ minHeight: '70vh' }}>
        <div className="container-site" style={{ padding: '48px 0 96px' }}>
          <p className="eyebrow mb-3" style={{ color: '#1a9fff' }}>
            {t('eyebrow')}
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <h1 className="display-section" style={{ marginBottom: 0 }}>
              {t('titulo')}
            </h1>
            {!loading && !erro && artigos.length > 0 && (
              <LayoutToggle layout={layout} onChange={handleLayoutChange} />
            )}
          </div>

          {loading && <p className="text-gray-500 text-sm">{t('carregando')}</p>}
          {!loading && erro && <p className="text-red-600 text-sm">{erro}</p>}
          {!loading && !erro && artigos.length === 0 && (
            <p className="text-gray-500 text-sm">{t('vazio')}</p>
          )}

          {!loading && !erro && artigos.length > 0 && layout === 'post' && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6 mx-auto"
              style={{ maxWidth: '860px' }}
            >
              {artigos.map((a) => (
                <ArtigoCardPost key={a.id} artigo={a} />
              ))}
            </div>
          )}

          {!loading && !erro && artigos.length > 0 && layout === 'grid3' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {artigos.map((a) => (
                <ArtigoCard key={a.id} artigo={a} />
              ))}
            </div>
          )}

          {!loading && !erro && artigos.length > 0 && layout === 'lista' && (
            <div>
              {artigos.map((a) => (
                <ArtigoLinha key={a.id} artigo={a} />
              ))}
            </div>
          )}
        </div>
      </main>
      <StoreFooter />
    </>
  );
}
